import { foldVi } from './master-data';

/**
 * Tách khối thông tin công ty NV copy từ email / chữ ký / Excel của khách (anh Tiến 07/10/2026):
 *
 *   CÔNG TY TNHH THÉP TRANG TRUNG AN
 *   Số 116 Nguyễn Văn Quỳ, Phường Phú Thuận, Thành phố Hồ
 *   Chí Minh, Việt Nam.
 *   Tel: 028 3773 1173 - Hotline :0902 919 736 - 0903831259
 *   Email: kinhdoanh@trangtrungan.com
 *
 * Chỉ dùng quy tắc (không gọi AI). Không nhận ra tên công ty kèm ít nhất một trường khác → null.
 * `rest`: các dòng không thuộc khối công ty (VD danh sách hàng dán cùng lúc), giữ nguyên thứ tự.
 */
export interface CompanyInfo {
  name: string;
  address: string | null;
  /** Số đầu tiên (thường là Tel/ĐT); các số sau ở `phones` */
  phone: string | null;
  phones: string[];
  email: string | null;
  taxCode: string | null;
  website: string | null;
  rest: string;
}

// Đầu dòng tên doanh nghiệp (so trên chữ đã bỏ dấu)
const NAME_START =
  /^(?:ten (?:cong ty|don vi|khach hang)\s*:?\s*)?(?:tong cong ty|cong ty|cty|ct tnhh|ct cp|doanh nghiep|dntn|ho kinh doanh|hkd|chi nhanh|cn cong ty|tap doan|xi nghiep|nha may|hop tac xa|htx|van phong dai dien)\b/;
const NAME_EN = /\b(?:co\.?,?\s*ltd|company|corporation|corp\.?|jsc|joint stock|limited|inc\.?|llc)\b/;
const NAME_LABEL = /^\s*(?:tên (?:công ty|đơn vị|khách hàng)|ten (?:cong ty|don vi|khach hang)|company(?: name)?)\s*:\s*/i;

const ADDRESS_LABEL = /^\s*(?:địa chỉ|dia chi|đ\/c|d\/c|đc|dc|add(?:ress)?|trụ sở|tru so|vp|văn phòng)\s*[:.]\s*/i;
// Từ hay gặp trong địa chỉ (đã bỏ dấu)
const ADDRESS_WORDS = /\b(?:so \d|duong|phuong|quan|huyen|xa|thi xa|thi tran|thanh pho|tp|tinh|kcn|khu cong nghiep|ccn|ap|khu pho|to dan pho|lo [a-z]?\d|viet nam|street|ward|district|province|city)\b/;

const PHONE_LABEL = /\b(?:tel|dt|dien thoai|sdt|so dt|hotline|mobile|mob|phone|di dong|dd)\b/;
const FAX_LABEL = /\bfax\b/;
const EMAIL = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/;
const TAX = /\b(?:mst|ma so thue|ma so doanh nghiep|msdn|tax(?: code| id)?)\b\s*:?\s*([\d][\d .-]{8,16}\d)/;
const WEBSITE = /\b(?:https?:\/\/)?(?:www\.)[\w-]+(?:\.[\w-]+)+\S*/i;
// Số điện thoại VN, cho phép 1 ký tự ngăn (cách, chấm, gạch) giữa các chữ số:
// cố định 02x + 8 số (11 số), di động 03/05/07/08/09 + 8 số (10 số); đầu +84 / 84 thay cho 0
const PHONE_RE = /(?<!\d)(?:\+?84[ .-]?|0)(?:2(?:[ .-]?\d){9}|[35789](?:[ .-]?\d){8})(?!\d)/g;

function cleanPhone(raw: string): string | null {
  let d = raw.replace(/\D/g, '');
  if (d.startsWith('84')) d = `0${d.slice(2)}`;
  if (d.length !== (d[1] === '2' ? 11 : 10)) return null;
  // Hiển thị: di động 0903 831 259; cố định HN/HCM 028 3773 1173; tỉnh khác 0274 373 7999
  if (/^02[48]/.test(d)) return `${d.slice(0, 3)} ${d.slice(3, 7)} ${d.slice(7)}`;
  return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
}

/** Các số điện thoại trong một dòng, bỏ số đứng sau chữ "Fax". */
function phonesIn(line: string): string[] {
  const out: string[] = [];
  const folded = foldVi(line.replace(/[^\p{L}\p{N}]+/gu, ' '));
  if (!PHONE_LABEL.test(` ${folded} `) && !FAX_LABEL.test(folded)) {
    // Dòng không có nhãn: chỉ nhận khi cả dòng gần như toàn số (VD "0903 831 259")
    if (/[\p{L}]{3,}/u.test(line)) return out;
  }
  // "(0274) 3 737 999" → bỏ ngoặc quanh mã vùng
  line = line.replace(/\((\+?\d{2,5})\)\s?/g, '$1 ');
  for (const m of line.matchAll(PHONE_RE)) {
    const before = foldVi(line.slice(0, m.index ?? 0));
    const lastFax = before.lastIndexOf('fax');
    const lastPhone = Math.max(...['tel', 'dt', 'dien thoai', 'hotline', 'mobile', 'phone', 'sdt', 'dd'].map((w) => before.lastIndexOf(w)));
    if (lastFax >= 0 && lastFax > lastPhone) continue;
    const p = cleanPhone(m[0]);
    if (p) out.push(p);
  }
  return out;
}

const isNameLine = (line: string) => {
  const f = foldVi(line);
  return NAME_START.test(f) || NAME_LABEL.test(line) || (NAME_EN.test(f) && !EMAIL.test(line));
};

const isFieldLine = (line: string) => {
  const f = foldVi(line);
  return (
    EMAIL.test(line) ||
    TAX.test(f) ||
    ADDRESS_LABEL.test(line) ||
    PHONE_LABEL.test(f) ||
    FAX_LABEL.test(f) ||
    /^\s*(?:website|web)\s*:/i.test(line) ||
    isNameLine(line)
  );
};

export function parseCompanyInfo(text: string): CompanyInfo | null {
  const lines = (text ?? '').replace(/\r/g, '').split('\n');
  const nameIdx = lines.findIndex((l) => l.trim() && isNameLine(l.trim()));
  if (nameIdx < 0) return null;
  const used = new Set<number>([nameIdx]);
  const name = lines[nameIdx]!.trim().replace(NAME_LABEL, '').replace(/[.,;:\s]+$/, '').replace(/\s+/g, ' ');
  if (name.length < 4 || name.length > 255) return null;

  let address: string | null = null;
  const phones: string[] = [];
  let email: string | null = null;
  let taxCode: string | null = null;
  let website: string | null = null;

  // Khối công ty: tên + tối đa 10 dòng sau (bỏ qua dòng trống), dừng khi gặp dòng không thuộc khối
  for (let i = nameIdx + 1, scanned = 0; i < lines.length && scanned < 10; i++) {
    const line = lines[i]!.trim();
    if (!line) continue;
    scanned++;
    const f = foldVi(line);
    let hit = false;

    const tax = TAX.exec(f);
    if (tax && !taxCode) {
      const d = tax[1]!.replace(/\D/g, '');
      if (d.length === 10 || d.length === 13) {
        taxCode = d.length === 13 ? `${d.slice(0, 10)}-${d.slice(10)}` : d;
        hit = true;
      }
    }
    const em = EMAIL.exec(line);
    if (em && !email) {
      email = em[0].toLowerCase();
      hit = true;
    }
    const web = WEBSITE.exec(line);
    if (web && !website && !em) {
      website = web[0];
      hit = true;
    }
    const ph = phonesIn(line);
    if (ph.length) {
      for (const p of ph) if (!phones.includes(p)) phones.push(p);
      hit = true;
    }
    if (!address && (ADDRESS_LABEL.test(line) || (!hit && ADDRESS_WORDS.test(f)))) {
      // Địa chỉ có thể xuống dòng: nối các dòng sau cho tới khi gặp trường khác (tối đa 2 dòng)
      let a = line.replace(ADDRESS_LABEL, '');
      // Dòng có cả địa chỉ và ĐT ("Đ/c: ... - ĐT: ...") → cắt phần ĐT
      a = a.split(/\s[-–|]\s*(?=(?:tel|đt|dt|điện thoại|hotline|fax|email|mst)\b)/i)[0]!;
      let j = i + 1;
      for (let k = 0; k < 2 && j < lines.length; k++, j++) {
        const next = lines[j]!.trim();
        if (!next || isFieldLine(next)) break;
        a = `${a.replace(/\s+$/, '')} ${next}`;
        used.add(j);
      }
      address = a.replace(/\s+/g, ' ').replace(/[.,;\s]+$/, '').trim() || null;
      used.add(i);
      i = j - 1;
      continue;
    }
    if (hit || /^\s*(?:website|web)\s*:/i.test(line)) {
      used.add(i);
      continue;
    }
    break; // hết khối công ty
  }

  if (!address && !phones.length && !email && !taxCode) return null;
  const rest = lines
    .filter((_, i) => !used.has(i))
    .join('\n')
    .replace(/^\s*\n+|\n+\s*$/g, '');
  return { name, address, phone: phones[0] ?? null, phones, email, taxCode, website, rest };
}
